import { useDispatch } from "react-redux";
import { baseStore } from "../store"; // Adjust the import based on your file structure
import Logger from "../helpers/logger";
import ErrorTracker from "../helpers/errorTracker";
import { enqueueNotification } from "../slice/notification/notificationSlice";
import { t } from "i18next";
import { reservationsApiSlice } from "../../api/reservations/reservationsApiSlice";
import { couponAppliedToReservations, reservationUpdated } from "../slice/bookingCreation/bookingCreationSlice";
import { notifyBookingFailure } from "../helpers/failureNotification";
import { INVALID_COUPON_KEY, voucherRefusalMessage } from "../../util/voucherRefusal";

interface AdditionalService {
  serviceId: string;
  name: string;
  quantity: number; // -1 means no quantity for the service
}

const updateReservationCall = async (
  reservationId: string,
  voucherId?: string,
  additionalServices?: AdditionalService[],
  totalPrice?: number
) => {
  const result = await baseStore.dispatch(
    reservationsApiSlice.endpoints.updateReservation.initiate({
      reservationId: reservationId,
      voucherId: voucherId,
      additionalServices: additionalServices,
      totalPrice: totalPrice,
    })
  );
  if ("error" in result && result.error) {
    // The response body stays on the error (`data`) so the caller can say why
    // a voucher was refused (#3052) — wrapping it in a message string lost it.
    throw Object.assign(
      new Error(
        `Failed to update Reservation for reservationId ${reservationId}: ${JSON.stringify(result.error)}`
      ),
      { data: (result.error as { data?: unknown }).data }
    );
  }
  return result;
};

/**
 * PATCHes the reservation the booking flow created and then pays.
 *
 * `reservationId` must be the one created in the CURRENT flow (the caller's
 * own state), never a value read back from storage: an id left over from an
 * earlier session — another room, other dates — was PATCHed here and the
 * payment step then failed on an empty reservation list. `null` means "this
 * flow has no reservation" (creation failed, or the page was reloaded on the
 * summary); the update refuses then, loudly, so the footer stays on the
 * summary instead of advancing to a payment with nothing to pay for.
 *
 * The returned function takes the id as an optional argument for a caller
 * that has only just created the reservation in the same handler (the
 * summary's retry of a failed creation), whose render-time id is still
 * `null`; without one it PATCHes `reservationId`.
 */
export const useUpdateReservation = (
  reservationId: string | null,
  voucherId?: string,
  additionalServices?: AdditionalService[],
  totalPrice?: number
) => {
  const dispatch = useDispatch();

  const updateReservation = async (
    forReservationId: string | null = reservationId
  ) => {
    if (!forReservationId) {
      const error = new Error("updateReservation called without a reservation of this flow");
      Logger.for("reservationChanged").error(error);
      ErrorTracker.trackException(error);
      notifyBookingFailure(dispatch, error);
      throw error;
    }
    try {
      const updatedReservation = await updateReservationCall(
        forReservationId,
        voucherId,
        additionalServices,
        totalPrice
      );
      dispatch(couponAppliedToReservations([updatedReservation.data[0]]));
      dispatch(reservationUpdated({ reservation: updatedReservation.data[0] }))
    } catch (error) {
      Logger.for("reservationChanged").error(error);
      ErrorTracker.trackException(error as Error);
      // #3052: a voucher the server refused (bound to another station,
      // expired, at its limit, ...) says why; every other failure keeps the
      // generic message.
      const refusal = voucherRefusalMessage(error);
      dispatch(
        enqueueNotification({
          severity: "error",
          message:
            refusal.key === INVALID_COUPON_KEY
              ? t("use_order.handovers.item_placement.error")
              : t(refusal.key, refusal.params),
        })
      );
      // Propagate the error (its only caller, the summary footer, catches it
      // and stays on the summary).
      throw error;
    }
  };

  return updateReservation;
};
